module Client.Admin.EditUser.Grid

open Shared
open Shared.Admin
open Client.Admin
open Client
open Elmish
open Illy.SafeStack.Client 
open Illy.SafeStack.Client.jQueryUI
open Fable.React
open Fable.React.Props
open Shared.Admin.Tags.StronglyTypedIds
open SafeStack.Client

type Model = 
    {
        Tags: Tags.Grid.Model
        Users: UserGrid.Model
        EditUser: EditUser.Model option
        EditTags: EditTags.Model option
    }

type Msg =
    | TagsMsg of Tags.Grid.Msg
    | UsersMsg of UserGrid.Msg
    | EditUser of UserId
    | EditTags of UserId
    | EditUserMsg of EditUser.Msg
    | EditTagsMsg of EditTags.Msg
    | CloseGridOverlay
    | EndNotification 

type Event =
    | ApiError of ApiError
    | CloseOverlay

let userGridFilters tags = 
    {UserGrid.IncludeUsersFilter.all with 
        Disabled = false
        Tags = tags |> Set.map TagId.unwrap
    }

let init () = 
    let um, uc = UserGrid.init (userGridFilters Set.empty)
    let tm, tc = Tags.Grid.init()
    {
        Users = um
        Tags = tm
        EditUser = None
        EditTags = None
    }, Cmd.batch [
        Cmd.map UsersMsg uc
        Cmd.map TagsMsg tc
    ]

let dispatchEditUserMsg model msg =
    match model.EditUser with 
    | None -> model, SideEffect.none
    | Some ma -> 
        let m, eff = EditUser.update msg ma   
        match eff with 
        | SideEffect.Cmd c -> {model with EditUser = Some m}, Cmd.map EditUserMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | EditUser.Event.ApiError err -> {model with EditUser = Some m}, err |> Event.ApiError |> SideEffect.Event
            | EditUser.Event.CloseOverlay -> {model with EditUser = None}, SideEffect.none
            | EditUser.Event.SaveSuccessful notification -> 
                {model with EditUser = None}, 
                Cmd.batch [
                    Notification.success notification EndNotification
                    UserGrid.RefreshGrid |> UsersMsg |> Cmd.ofMsg
                ] |> SideEffect.Cmd

let dispatchEditTagsMsg model msg =
    match model.EditTags with 
    | None -> model, SideEffect.none
    | Some ma -> 
        let m, eff = EditTags.update msg ma   
        match eff with 
        | SideEffect.Cmd c -> {model with EditTags = Some m}, Cmd.map EditTagsMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | EditTags.Event.ApiError err -> {model with EditTags = Some m}, err |> Event.ApiError |> SideEffect.Event
            | EditTags.Event.CloseOverlay -> {model with EditTags = None}, SideEffect.none
            | EditTags.Event.SaveSuccessful notification -> 
                {model with EditTags = None}, 
                Cmd.batch [
                    Notification.success notification EndNotification
                    UserGrid.RefreshGrid |> UsersMsg |> Cmd.ofMsg
                ] |> SideEffect.Cmd


let dispatchUsersMsg model uMsg =
    let m, eff = UserGrid.update uMsg model.Users
    {model with Users = m},
    match eff with 
    | SideEffect.Cmd c -> Cmd.map UsersMsg c |> SideEffect.Cmd
    | SideEffect.Event ev ->
        match ev with
        | UserGrid.Event.ApiError err -> err |> ApiError |> SideEffect.Event
        | UserGrid.Event.CloseOverlay -> CloseOverlay |> SideEffect.Event

let dispatchTagsMsg model tMsg = 
    let m, eff = Tags.Grid.update tMsg model.Tags
    match eff with 
    | SideEffect.Cmd c -> {model with Tags = m}, Cmd.map TagsMsg c |> SideEffect.Cmd
    | SideEffect.Event ev -> 
        match ev with 
        | Tags.Grid.Event.SelectedTagsUpdated tags -> 
            {model with Tags = m}, Cmd.ofMsg (UserGrid.Msg.UpdateFilters (userGridFilters tags)) |> Cmd.map UsersMsg |> SideEffect.Cmd
        | Tags.Grid.Event.ApiError err ->
            {model with Tags = m}, SideEffect.Event (ApiError err)

let update msg model = 
    match msg with 
    | UsersMsg uMsg -> dispatchUsersMsg model uMsg
    | TagsMsg tMsg -> dispatchTagsMsg model tMsg
    | EditUser userId -> 
        let m, c = EditUser.init userId
        {model with EditUser = Some m}, Cmd.map EditUserMsg c |> SideEffect.Cmd
    | EditUserMsg lMsg -> dispatchEditUserMsg model lMsg
    | EditTags userId -> 
        let m, c = EditTags.init userId
        {model with EditTags = Some m}, Cmd.map EditTagsMsg c |> SideEffect.Cmd
    | EditTagsMsg lMsg -> dispatchEditTagsMsg model lMsg
    | EndNotification -> model, SideEffect.none
    | CloseGridOverlay -> model, SideEffect.Event Event.CloseOverlay

let private renderGridOverlay model dispatch  = 
    let title = 
        if UserGrid.isLoadingMoreResults model.Users then "Loading more results..."
        else "Manage Users"
    jQueryUI.modal title 50 Msg.CloseGridOverlay dispatch (Some 800) [

        Tags.Grid.renderGrid model.Tags (TagsMsg >> dispatch)

        UserGrid.viewContent model.Users (UsersMsg >> dispatch) [
            None ===> fun u -> [ 
                a [
                    OnClick (fun _ -> dispatch (EditUser (UserId u.Id)))
                ] [
                    str "Edit"
                ]
            ]
            UserGrid.GridColumn.name (UserGrid.isLoadingMoreResults model.Users) model.Users.SortInfo (UsersMsg >> dispatch)
            UserGrid.GridColumn.username (UserGrid.isLoadingMoreResults model.Users) model.Users.SortInfo (UsersMsg >> dispatch)
            Some [ str "Actions" ] ===> fun u -> [ UI.button "Edit Tags" (EditTags (UserId u.Id)) dispatch ]
        ]
    ]

let view (model: Model) dispatch =
    [
        if Tags.Grid.hasOpenOverlay model.Tags |> not && model.EditUser.IsNone && model.EditTags.IsNone then
            yield! renderGridOverlay model dispatch
        yield! Tags.Grid.renderOverlays model.Tags (TagsMsg >> dispatch)
        yield! jQueryUI.conditionallyRenderView model.EditUser EditUser.view EditUserMsg dispatch
        yield! jQueryUI.conditionallyRenderView model.EditTags EditTags.view EditTagsMsg dispatch
    ]
    
